import React, { lazy } from 'react';
import SettingsIcon from '@mui/icons-material/Settings';
import ProtectedRoute from 'src/pages/utility/ProtectedRoute';
import { getCrumb } from 'src/components/MainPage/_shared/Breadcrumbs/Breadcrumb';
import { roleSet } from 'src/constants/role';
import type { AppRoute } from '../types';
import { SuspenseContainer } from 'src/components/MainPage/_shared/page';
import { syncTabs } from 'src/pages/management/SyncPage/SyncTabs';

const AuditPromoCodesPage = lazy(
  () => import('src/pages/management/AuditPromoCodesPage')
);
const AuditPage = lazy(() => import('src/pages/management/AuditPage'));
const ShopsPage = lazy(() => import('src/pages/management/ShopsPage'));
const ShopPage = lazy(() => import('src/pages/management/ShopPage'));
const MangoPage = lazy(() => import('src/pages/management/MangoPage'));
const RestrictionsPage = lazy(
  () => import('src/pages/management/RestrictionsPage')
);
const RestrictionPage = lazy(
  () => import('src/pages/management/RestrictionPage')
);
const KitchenChainsPage = lazy(
  () => import('src/pages/management/KitchenChainsPage')
);
const KitchenChainPage = lazy(
  () => import('src/pages/management/KitchenChainPage')
);
const SettingsPage = lazy(() => import('src/pages/management/SettingsPage'));
const CitiesPage = lazy(() => import('src/pages/management/CitiesPage'));
const CityPage = lazy(() => import('src/pages/management/CityPage'));
const MapFramesPage = lazy(() => import('src/pages/management/MapFramesPage'));
const MapFramePage = lazy(() => import('src/pages/management/MapFramePage'));
const EntrepreneursPage = lazy(
  () => import('src/pages/management/EntrepreneursPage')
);
const EntrepreneurPage = lazy(
  () => import('src/pages/management/EntrepreneurPage')
);
const SyncPage = lazy(() => import('src/pages/management/SyncPage'));
const ZonesPage = lazy(() => import('src/pages/management/ZonesPage'));
const FranchiseesPage = lazy(
  () => import('src/pages/management/FranchiseesPage')
);
const FranchiseePage = lazy(
  () => import('src/pages/management/FranchiseePage')
);
const PreparationIntervalsPage = lazy(
  () => import('src/pages/management/PreparationIntervalsPage')
);
const PreparationIntervalPage = lazy(
  () => import('src/pages/management/PreparationIntervalPage')
);

export const managementRoutes: AppRoute[] = [
  {
    element: <ProtectedRoute roles={roleSet.cashierAndOperatorToSuperAdmin} />,
    handle: {
      label: 'Управление',
      icon: <SettingsIcon />,
      roles: roleSet.cashierAndOperatorToSuperAdmin,
    },
    children: [
      {
        path: `audit-promocodes`,
        element: <AuditPromoCodesPage />,
        handle: {
          label: 'Журнал-промокоды',
          roles: roleSet.cashierAndOperatorToSuperAdmin,
        },
      },
      {
        element: <ProtectedRoute roles={roleSet.adminToSuperAdmin} />,
        children: [
          {
            path: `audit`,
            element: <AuditPage />,
            handle: {
              label: 'Журнал',
              roles: roleSet.adminToSuperAdmin,
            },
          },
          {
            element: <ProtectedRoute roles={roleSet.adminToSuperAdmin} />,
            children: [
              {
                path: `shops`,
                handle: {
                  crumb: getCrumb('Точки продаж', true),
                },
                children: [
                  {
                    index: true,
                    element: <ShopsPage />,
                    handle: {
                      label: 'Точки продаж',
                      roles: roleSet.adminToSuperAdmin,
                    },
                  },
                  {
                    path: `:id`,
                    element: (
                      <SuspenseContainer>
                        <ShopPage />
                      </SuspenseContainer>
                    ),
                    async lazy() {
                      const { shopLoader } = await import(
                        'src/pages/management/ShopPage/shopLoader'
                      );
                      return {
                        loader: shopLoader,
                      };
                    },
                  },
                ],
              },
            ],
          },
          {
            path: `zones`,
            handle: {
              crumb: getCrumb('Зоны доставки', true),
            },
            children: [
              {
                index: true,
                element: <ZonesPage />,
                handle: {
                  label: 'Зоны доставки',
                  roles: roleSet.adminToSuperAdmin,
                },
              },
            ],
          },
          {
            path: `cities`,
            handle: {
              crumb: getCrumb('Города', true),
            },
            children: [
              {
                index: true,
                element: <CitiesPage />,
                handle: {
                  label: 'Города',
                  roles: roleSet.adminToSuperAdmin,
                  nonFranchiseeAccessOnly: true,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <CityPage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { cityLoader } = await import(
                    'src/pages/management/CityPage/cityLoader'
                  );
                  return {
                    loader: cityLoader,
                  };
                },
              },
            ],
          },
          {
            path: `map-frames`,
            handle: {
              crumb: getCrumb('Карты', true),
            },
            children: [
              {
                index: true,
                element: <MapFramesPage />,
                handle: {
                  label: 'Карты',
                  roles: roleSet.adminToSuperAdmin,
                  nonFranchiseeAccessOnly: true,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <MapFramePage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { mapFrameLoader } = await import(
                    'src/pages/management/MapFramePage/mapFrameLoader'
                  );
                  return {
                    loader: mapFrameLoader,
                  };
                },
              },
            ],
          },
          {
            path: `mango`,
            element: <MangoPage />,
            handle: {
              label: 'Манго',
              roles: roleSet.adminToSuperAdmin,
              nonFranchiseeAccessOnly: true,
            },
          },
          {
            path: `restrictions`,
            handle: {
              crumb: getCrumb('Ограничения продаж', true),
            },
            children: [
              {
                index: true,
                element: <RestrictionsPage />,
                handle: {
                  label: 'Ограничения продаж',
                  roles: roleSet.adminToSuperAdmin,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <RestrictionPage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { restrictionLoader } = await import(
                    'src/pages/management/RestrictionPage/restrictionLoader'
                  );
                  return {
                    loader: restrictionLoader,
                  };
                },
              },
            ],
          },
          {
            path: `kitchen-chains`,
            handle: {
              crumb: getCrumb('Цепочки кухни', true),
            },
            children: [
              {
                index: true,
                element: <KitchenChainsPage />,
                handle: {
                  label: 'Цепочки кухни',
                  roles: roleSet.adminToSuperAdmin,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <KitchenChainPage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { kitchenChainLoader } = await import(
                    'src/pages/management/KitchenChainPage/kitchenChainLoader'
                  );
                  return {
                    loader: kitchenChainLoader,
                  };
                },
              },
            ],
          },
          {
            path: `preparation-intervals`,
            handle: {
              crumb: getCrumb('Интервалы', true),
            },
            children: [
              {
                index: true,
                element: <PreparationIntervalsPage />,
                handle: {
                  label: 'Интервалы',
                  roles: roleSet.adminToSuperAdmin,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <PreparationIntervalPage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { preparationIntervalLoader } = await import(
                    'src/pages/management/PreparationIntervalPage/preparationIntervalLoader'
                  );
                  return {
                    loader: preparationIntervalLoader,
                  };
                },
              },
            ],
          },
          {
            path: `settings`,
            element: <SettingsPage />,
            handle: {
              label: 'Настройки',
              roles: roleSet.adminToSuperAdmin,
              nonFranchiseeAccessOnly: true,
            },
            async lazy() {
              const { settingsLoader } = await import(
                'src/pages/management/SettingsPage/settingsLoader'
              );
              return {
                loader: settingsLoader,
              };
            },
          },
          {
            path: `entrepreneurs`,
            handle: {
              crumb: getCrumb('Юридические лица', true),
            },
            children: [
              {
                index: true,
                element: <EntrepreneursPage />,
                handle: {
                  label: 'Юридические лица',
                  roles: roleSet.superAdmin,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <EntrepreneurPage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { entrepreneurLoader } = await import(
                    'src/pages/management/EntrepreneurPage/entrepreneurLoader'
                  );
                  return {
                    loader: entrepreneurLoader,
                  };
                },
              },
            ],
          },
          {
            path: `franchisees`,
            handle: {
              crumb: getCrumb('Франчайзи', true),
            },
            children: [
              {
                index: true,
                element: <FranchiseesPage />,
                handle: {
                  label: 'Франчайзи',
                  roles: roleSet.superAdmin,
                },
              },
              {
                path: `:id`,
                element: (
                  <SuspenseContainer>
                    <FranchiseePage />
                  </SuspenseContainer>
                ),
                async lazy() {
                  const { franchiseeLoader } = await import(
                    'src/pages/management/FranchiseePage/franchiseeLoader'
                  );
                  return {
                    loader: franchiseeLoader,
                  };
                },
              },
            ],
          },
          {
            path: `sync`,
            element: <SyncPage />,
            handle: {
              label: 'Синхронизация',
              roles: roleSet.adminToSuperAdmin,
              nonFranchiseeAccessOnly: true,
            },
            children: syncTabs,
          },
        ],
      },
    ],
  },
];
